/**
 * What a person setting up an organization's Stripe or PayPal account has to get right,
 * stated once for the contract that refuses it and the screen that warns about
 * it — docs/decisions/0098. Pure, because it is bundled into the admin.
 */

/**
 * The webhook events each provider must send, and **exactly** the ones
 * `stripe-webhook.ts` and `paypal-webhook.ts` read: the setup screen lists them
 * to subscribe to, and the verifier checks a registered endpoint against them.
 * Anything else a provider sends is answered 2xx and written nowhere.
 */
export const STRIPE_WEBHOOK_EVENT = {
  checkoutCompleted: 'checkout.session.completed',
  checkoutAsyncSucceeded: 'checkout.session.async_payment_succeeded',
  offSessionSucceeded: 'payment_intent.succeeded',
  refunded: 'charge.refunded',
  disputeClosed: 'charge.dispute.closed',
} as const;

export const PAYPAL_WEBHOOK_EVENT = {
  captureCompleted: 'PAYMENT.CAPTURE.COMPLETED',
  captureRefunded: 'PAYMENT.CAPTURE.REFUNDED',
  disputeResolved: 'CUSTOMER.DISPUTE.RESOLVED',
} as const;

export const PROVIDER_WEBHOOK_EVENTS: Readonly<{
  stripe: readonly string[];
  paypal: readonly string[];
}> = {
  stripe: Object.values(STRIPE_WEBHOOK_EVENT),
  paypal: Object.values(PAYPAL_WEBHOOK_EVENT),
};

export type CredentialProvider = 'stripe' | 'paypal';

export type CredentialField =
  | 'secretKey'
  | 'webhookSecret'
  | 'clientId'
  | 'clientSecret'
  | 'webhookId';

export const CREDENTIAL_FIELDS: Readonly<Record<CredentialProvider, readonly CredentialField[]>> = {
  stripe: ['secretKey', 'webhookSecret'],
  paypal: ['clientId', 'clientSecret', 'webhookId'],
};

export const PAYMENT_CREDENTIAL_FIELD_REQUIRED = 'PAYMENT_CREDENTIAL_FIELD_REQUIRED';
export const PAYMENT_CREDENTIAL_FIELD_WHITESPACE = 'PAYMENT_CREDENTIAL_FIELD_WHITESPACE';
export const PAYMENT_CREDENTIAL_FIELD_TOO_SHORT = 'PAYMENT_CREDENTIAL_FIELD_TOO_SHORT';
export const PAYMENT_CREDENTIAL_FIELD_TOO_LONG = 'PAYMENT_CREDENTIAL_FIELD_TOO_LONG';
export const PAYMENT_CREDENTIAL_FIELD_FOREIGN = 'PAYMENT_CREDENTIAL_FIELD_FOREIGN';
export const STRIPE_SECRET_KEY_PUBLISHABLE = 'STRIPE_SECRET_KEY_PUBLISHABLE';
export const STRIPE_SECRET_KEY_SHAPE = 'STRIPE_SECRET_KEY_SHAPE';
export const STRIPE_WEBHOOK_SECRET_SHAPE = 'STRIPE_WEBHOOK_SECRET_SHAPE';
export const PAYPAL_WEBHOOK_ID_SHAPE = 'PAYPAL_WEBHOOK_ID_SHAPE';

/** Every reason a credential's own shape is refused with, before any provider is asked. */
export const PAYMENT_CREDENTIAL_SHAPE_REASONS = [
  PAYMENT_CREDENTIAL_FIELD_REQUIRED,
  PAYMENT_CREDENTIAL_FIELD_WHITESPACE,
  PAYMENT_CREDENTIAL_FIELD_TOO_SHORT,
  PAYMENT_CREDENTIAL_FIELD_TOO_LONG,
  PAYMENT_CREDENTIAL_FIELD_FOREIGN,
  STRIPE_SECRET_KEY_PUBLISHABLE,
  STRIPE_SECRET_KEY_SHAPE,
  STRIPE_WEBHOOK_SECRET_SHAPE,
  PAYPAL_WEBHOOK_ID_SHAPE,
] as const;

export type PaymentCredentialShapeReason = (typeof PAYMENT_CREDENTIAL_SHAPE_REASONS)[number];

const LENGTH: Readonly<Record<CredentialField, { min: number; max: number }>> = {
  secretKey: { min: 20, max: 512 },
  webhookSecret: { min: 20, max: 512 },
  clientId: { min: 16, max: 256 },
  clientSecret: { min: 16, max: 256 },
  webhookId: { min: 8, max: 64 },
};

const STRIPE_SECRET_PREFIXES = ['sk_live_', 'sk_test_', 'rk_live_', 'rk_test_'] as const;
const STRIPE_TEST_PREFIXES = ['sk_test_', 'rk_test_'] as const;
const STRIPE_RESTRICTED_PREFIXES = ['rk_live_', 'rk_test_'] as const;

/**
 * Why one field's value cannot be a key of its kind, or null. The value is
 * expected trimmed: leading and trailing whitespace is what a paste carries and
 * is dropped, but **whitespace inside a key is refused**, because no provider
 * issues one and a key with a stray space in it fails only when a donor pays.
 */
export function credentialFieldProblem(
  field: CredentialField,
  value: string | undefined,
): PaymentCredentialShapeReason | null {
  if (value === undefined || value === '') return PAYMENT_CREDENTIAL_FIELD_REQUIRED;
  if (/\s/.test(value)) return PAYMENT_CREDENTIAL_FIELD_WHITESPACE;
  if (field === 'secretKey') {
    if (value.startsWith('pk_')) return STRIPE_SECRET_KEY_PUBLISHABLE;
    if (!STRIPE_SECRET_PREFIXES.some((prefix) => value.startsWith(prefix))) {
      return STRIPE_SECRET_KEY_SHAPE;
    }
  }
  if (field === 'webhookSecret' && !value.startsWith('whsec_')) return STRIPE_WEBHOOK_SECRET_SHAPE;
  if (field === 'webhookId' && !/^[A-Za-z0-9_-]+$/.test(value)) return PAYPAL_WEBHOOK_ID_SHAPE;
  const { min, max } = LENGTH[field];
  if (value.length < min) return PAYMENT_CREDENTIAL_FIELD_TOO_SHORT;
  if (value.length > max) return PAYMENT_CREDENTIAL_FIELD_TOO_LONG;
  return null;
}

/** A Stripe key from test mode: it charges nobody, and production refuses it. */
export function isStripeTestKey(secretKey: string): boolean {
  return STRIPE_TEST_PREFIXES.some((prefix) => secretKey.startsWith(prefix));
}

/** A restricted key works only if it was granted the permissions a checkout needs. */
export function isStripeRestrictedKey(secretKey: string): boolean {
  return STRIPE_RESTRICTED_PREFIXES.some((prefix) => secretKey.startsWith(prefix));
}
